/* global google */
import _ from 'underscore';
import polyline from 'polyline';


const batchSize = 10; // 10 is the max with no API key

const combineOverlapping = (overlap, list) =>
  list.reduce((previous, current, i) => {
    if (i < overlap) {
      return previous.concat(current);
    }
    return previous.concat(current.slice(overlap));
  }, []);

class DirectionsService {
  constructor(options = {}) {
    this.directionsService = options.router || new google.maps.DirectionsService();
  }

  route(coordinates, snapping = []) {
    if (coordinates.length < 2) {
      return new Promise((resolve) => {
        resolve({
          geometry: polyline.encode(coordinates.map((coord) => [coord.lat, coord.lng])),
          waypoints: [0],
        });
      });
    }

    const promises = [];
    // Batch the directions requests, overlapping each set of coordinates by one point
    for (let i = 0; i < coordinates.length - 1; i += batchSize - 1) {
      promises.push(this._route(coordinates.slice(i, i + batchSize)));
    }

    // After all the routing requests come back, combine the results
    return Promise.all(promises).then((results) => {
      // Flatten results into a list of all segments
      const allSegments = results.reduce((previous, current) => previous.concat(current), []);
      // Replace non-snapped segments with straight lines
      const segments = allSegments.map((result, i) => {
        if (snapping.length < coordinates.length || (snapping[i] && snapping[i + 1])) {
          return result;
        }
        // Use the original coordinate instead of the routed one if the
        // waypoint isn't snapped
        const pair = [];
        if (snapping[i]) {
          pair.push(result[0]);
        } else {
          pair.push(new google.maps.LatLng(coordinates[i]));
        }
        if (snapping[i + 1]) {
          pair.push(_.last(result));
        } else {
          pair.push(new google.maps.LatLng(coordinates[i + 1]));
        }
        return pair;
      });

      const unsignedWaypoints = segments.reduce((previous, current) => {
        const previousWaypoint = _.last(previous);
        previous.push(previousWaypoint + current.length - 1);
        return previous;
      }, [0]);
      const waypoints = unsignedWaypoints.map((waypoint, i) => {
        if (snapping.length < coordinates.length || snapping[i]) return waypoint;
        return -waypoint;
      });
      return {
        geometry: google.maps.geometry.encoding.encodePath(combineOverlapping(1, segments)),
        waypoints,
      };
    });
  }

  _route(coordinates) {
    const latLngs = coordinates.map((coord) => new google.maps.LatLng(coord.lat, coord.lng));

    if (latLngs.length < 2) {
      return new Promise((resolve) => resolve([latLngs]));
    }

    const waypoints = latLngs.slice(1, latLngs.length - 1).map((location) => ({location}));

    const request = {
      origin: latLngs[0],
      destination: latLngs[latLngs.length - 1],
      waypoints,
      travelMode: google.maps.TravelMode.DRIVING,
    };

    return new Promise((resolve, reject) => {
      this.directionsService.route(request, (directions, statusType) => {
        if (statusType === 'OK') {
          const d = this._parseDirections(directions);
          resolve(d);
        } else {
          reject(statusType);
        }
      });
    });
  }

  _parseDirections(directions) {
    // Take the legs from the first route option and create a Coordinate list
    // for the entire route.
    return directions.routes[0].legs.map((leg) =>
      combineOverlapping(1, leg.steps.map((step) => step.lat_lngs))
    );
  }
}


export default DirectionsService;



// WEBPACK FOOTER //
// ./src/common/services/DirectionsService.js